export interface DashboardOverview {
  avg_time_to_shortlist: number;
  jobs_with_no_matches: number;
  total_candidates_in_pipeline: number;
}

export interface DepartmentData {
  department: string;
  reviewed_job_posts: number;
}

export interface DailyActivity {
  reviews_completed: {
    department_data: DepartmentData[];
    reviewed_job_posts: number;
    total_job_posts: number;
  };
  actions_to_take: {
    pending_reviews: number;
  };
}

export interface CandidatePipelineFunnel {
  matched: number;
  reviewed: number;
  shortlisted: number;
  declined: number;
}

export interface JobWithNoMatches {
  id: number;
  job_title: string;
  published_since: number;
}

export interface MatchPendingReview {
  match_id: number;
  job_post_id: number;
  job_title: string;
  match_score: number;
  candidate_id: number;
  candidate_name: string;
}

export interface HiringManagerNotResponding {
  hiring_manager_id: number;
  hiring_manager_name: string;
  department: string;
  job_title: string;
  inactive_since: number;
}

export interface DashboardData {
  overview: DashboardOverview;
  daily_activity: DailyActivity;
  candidate_pipeline_funnel: CandidatePipelineFunnel;
  jobs_with_no_matches: JobWithNoMatches[];
  matches_pending_review: MatchPendingReview[];
  hiring_manager_not_responding: HiringManagerNotResponding[];
}

export interface DashboardResponse {
  message: string;
  code: number;
  data: DashboardData;
  error: Record<string, unknown>;
}

export async function fetchDashboard(
  accessToken: string
): Promise<{ data?: DashboardData; error?: string }> {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const endpoint = `${API_BASE_URL}/api/dashboard/recruiter/dashboard/`;

  const { authenticatedFetch } = await import("./auth/apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`
    },
  });

  const responseBody: DashboardResponse = await response.json().catch(() => null);
  if (!response.ok) {
    return { error: (responseBody && responseBody.message) || 'Failed to fetch dashboard data.' };
  }
  return { data: responseBody?.data };
}

// ================= Super Admin Dashboard =================

export interface SuperAdminDashboardSeries {
  week_labels: string[];
  month_labels: string[];
  companies: {
    companies_weekly: number[];
    companies_monthly: number[];
  };
  jobs: {
    jobs_weekly: number[];
    jobs_monthly: number[];
  };
  candidates: {
    candidates_weekly: number[];
    candidates_monthly: number[];
  };
  matches: {
    matches_weekly_avg: number[];
    matches_monthly_avg: number[];
  };
}

export interface SuperAdminDashboardResponse {
  message: string;
  code: number;
  data: SuperAdminDashboardSeries;
  error: Record<string, unknown>;
}

export async function fetchSuperAdminDashboard(
  accessToken: string
): Promise<{ data?: SuperAdminDashboardSeries; error?: string }> {
  try {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const currentYear = new Date().getFullYear();
    const endpoint = `${API_BASE_URL}/api/dashboard/super-admin/dashboard/?year_start=${currentYear}`;

    const { authenticatedFetch } = await import("./auth/apiInterceptor");
    const response = await authenticatedFetch(endpoint, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`
      },
    });

    const responseBody: SuperAdminDashboardResponse = await response.json().catch(() => null);
    if (!response.ok) {
      return { error: (responseBody && responseBody.message) || 'Failed to fetch super admin dashboard data.' };
    }

    return { data: responseBody?.data };
  } catch (error) {
    // Handle network errors and other exceptions
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch super admin dashboard data.';
    console.error("Error fetching super admin dashboard:", error);
    return { error: errorMessage };
  }
}
